import { CreatorInfo } from "@/components/CreatorInfo";
import { QuotationRevisions } from "@/components/QuotationRevisions";
import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { CreateLinkedDocumentDialog } from "@/components/CreateLinkedDocumentDialog";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { fmtDate, fmtMoney } from "@/lib/format";
import { toast } from "sonner";
import { FileText, Download } from "lucide-react";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { RowDeleteButton } from "@/components/RowDeleteButton";
import { SentEmailChip, SentEmailBlock } from "@/components/SentEmail";
import { EmailSendButton } from "@/components/EmailSendButton";
import { FlagButton, FlagBadge } from "@/components/FlagButton";

const STATUS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  invoiced: "bg-blue-50 text-blue-700 border-blue-200",
  void: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Proformas() {
  const [rows, setRows] = useState([]);
  const [converting, setConverting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const convert = async () => {
    setConverting(true);
    try { const { data } = await api.post(`/documents/proformas/${selected.id}/convert`); toast.success("Draft invoice created on the same Sale"); navigate(`/invoices?id=${data.invoice_id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setConverting(false); }
  };
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sqlDlg, setSqlDlg] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [emailed, setEmailed] = useState("");
  const [searchParams] = useSearchParams();
  const linkedId = searchParams.get("id");
  useEffect(() => {
    if (linkedId) setSelected(rows.find((r) => r.id === linkedId) || null);
  }, [linkedId, rows]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (emailed) params.emailed = emailed;
    const { data } = await api.get("/proformas", { params });
    setRows(data); setLoading(false);
  }, [emailed]);
  useEffect(() => { load(); }, [load]);

  const openDetail = (r) => setSelected(r);

  const act = async (id, path) => {
    try {
      const { data } = await api.post(`/proformas/${id}/${path}`);
      toast.success(`Pro-forma ${path}ed`);
      await load(); if (selected?.id === id) setSelected(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <>
      <PageHeader title="Quotations & Pro-formas" subtitle="Offer or request payment first. Confirm, then convert to a draft invoice on the same Sale.">
        <Button onClick={() => setOpenCreate(true)} data-testid="new-proforma-button"><FileText className="w-4 h-4 mr-1.5" /> New quotation / pro-forma</Button>
      </PageHeader>
      <PageBody>
        <div className="flex items-center gap-3 mb-4">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Emailed</Label>
          <Select value={emailed || "all"} onValueChange={(v) => setEmailed(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="proforma-filter-emailed"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Emailed</SelectItem>
              <SelectItem value="no">Not emailed</SelectItem>
            </SelectContent>
          </Select>
          {emailed && <Button variant="ghost" size="sm" onClick={() => setEmailed("")}>Clear</Button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Number</TableHead>
                <TableHead>Sale</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>SQL Invoice</TableHead>
                <TableHead>Last emailed</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">No pro-formas yet. Generate one from a sale.</TableCell></TableRow>}
              {rows.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => openDetail(p)} data-testid={`proforma-row-${p.number}`}>
                  <TableCell className="font-mono text-xs">{p.number}<div className="text-[10px] text-slate-500 font-sans mt-1">{p.document_type === "quotation" ? "Quotation" : "Pro-forma"}</div></TableCell>
                  <TableCell className="font-mono text-xs">{p.sale_number}</TableCell>
                  <TableCell>{p.buyer_snapshot?.company || p.buyer_snapshot?.name || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={STATUS[p.status]}>{p.status.toUpperCase()}</Badge></TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtMoney(p.total_minor, p.currency)}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDate(p.issue_date)}</TableCell>
                  <TableCell className="text-xs font-mono">{p.sql_ref?.sql_invoice_number || "—"}</TableCell>
                  <TableCell><SentEmailChip record={p} testId={`proforma-last-emailed-${p.id}`} /></TableCell>
                  <TableCell className="w-10 py-1" onClick={(e) => e.stopPropagation()}>
                    <RowDeleteButton
                      endpoint={`/proformas/${p.id}`}
                      label={`pro-forma ${p.number}`}
                      onDeleted={load}
                      testId={`proforma-delete-${p.number}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </PageBody>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="min-w-[500px] overflow-y-auto" data-testid="proforma-detail-sheet">
          <SheetHeader>
            <SheetTitle className="font-mono" data-testid="proforma-detail-title">{selected?.number || "Pro-forma details"}</SheetTitle>
            <SheetDescription data-testid="proforma-detail-description">{selected?.buyer_snapshot?.name || "Review the pro-forma and its linked Sale."}</SheetDescription>
          </SheetHeader>
          {selected && (
            <>
              <div className="mt-6 space-y-5 text-sm">
                <Badge variant="outline" className={STATUS[selected.status]}>{selected.status.toUpperCase()}</Badge>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <Info label="Sale"><Link to={`/sales?id=${selected.sale_id}`} className="text-blue-700 underline" data-testid="proforma-linked-sale">{selected.sale_number}</Link></Info>
                  <Info label="Issued">{fmtDate(selected.issue_date)}</Info>
                  <Info label="Bill To">{selected.buyer_snapshot?.company || selected.buyer_snapshot?.name}</Info>
                  {selected.student_id && <Info label="Student / Learner"><Link to={`/students?id=${selected.student_id}`} className="text-blue-700 underline" data-testid="proforma-student-link">View student profile</Link></Info>}
                  <Info label="SST"><span data-testid="proforma-sst-summary">{selected.sst_rate ?? 0}% · {fmtMoney(selected.services_tax_minor ?? selected.lines_snapshot.reduce((sum, l) => sum + (l.tax_minor || 0), 0), selected.currency)}</span></Info>
                  <Info label="Price treatment"><span data-testid="proforma-pricing-mode">{selected.pricing_mode === "inclusive" ? "SST included in prices" : "SST added on top"}</span></Info>
                  <Info label="Email">{selected.buyer_snapshot?.email || "—"}</Info>
                  <Info label="Total"><span className="font-mono">{fmtMoney(selected.total_minor, selected.currency)}</span></Info>
                  <Info label="Currency">{selected.currency}</Info>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Lines</div>
                  <div className="border rounded overflow-hidden">
                    <Table>
                      <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="text-right w-16">Qty</TableHead><TableHead className="text-right w-28">Amount</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {selected.lines_snapshot.map((l, i) => (
                          <TableRow key={`${l.description || "line"}-${i}`}>
                            <TableCell className="text-xs">{l.description}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{l.quantity}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{fmtMoney(l.line_total_minor, selected.currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                {selected.sql_ref && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">SQL Accounting reference</div>
                    <div className="border border-slate-200 rounded p-3 text-xs font-mono">
                      <div>Invoice: {selected.sql_ref.sql_invoice_number}</div>
                      <div>Issued: {selected.sql_ref.issued_date}</div>
                      {selected.sql_ref.einvoice_reference && <div>e-Invoice: {selected.sql_ref.einvoice_reference}</div>}
                    </div>
                  </div>
                )}
                <CreatorInfo record={selected} kind="proforma" />
                <SentEmailBlock record={selected} testId="proforma-sent-email-block" />
                <QuotationRevisions document={selected} onChanged={async (d) => { await load(); setSelected(d); }} />
                <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                  {selected.status === "draft" && (
                    <Button size="sm" onClick={() => act(selected.id, "confirm")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-proforma">Confirm</Button>
                  )}
                  {selected.status !== "void" && !selected.invoice_id && (
                    <Button size="sm" variant="outline" onClick={() => act(selected.id, "void")}>Void</Button>
                  )}
                  {selected.status === "confirmed" && !selected.sql_ref && ["superadmin", "owner", "accounts"].includes(user.role) && <Button size="sm" disabled={converting} onClick={convert} data-testid="convert-proforma-invoice">{converting ? "Converting…" : "Convert to invoice"}</Button>}
                  {selected.invoice_id && <Button asChild size="sm" variant="outline"><Link to={`/invoices?id=${selected.invoice_id}`} data-testid="proforma-converted-invoice">Open converted invoice</Link></Button>}
                  {!selected.invoice_id && (selected.status === "confirmed" || selected.status === "invoiced") && (
                    <Button size="sm" variant="outline" onClick={() => setSqlDlg(selected)} data-testid="attach-sql-ref">
                      {selected.status === "invoiced" ? "Update SQL ref" : "Attach SQL invoice"}
                    </Button>
                  )}
                  <a href={`${API}/proformas/${selected.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid="download-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
                  </a>
                  {selected.status !== "void" && (
                    <EmailSendButton
                      resource="proformas"
                      id={selected.id}
                      defaultTo={selected.buyer_snapshot?.email || ""}
                      label={selected.document_type === "quotation" ? "quotation" : "pro-forma"}
                      triggerTestId="send-proforma"
                      onSent={async (data) => { await load(); setSelected(data); }}
                    />
                  )}
                  <DeleteRecordButton
                    resource="proformas"
                    id={selected.id}
                    status={selected.status}
                    label={`${selected.document_type === "quotation" ? "quotation" : "pro-forma"} ${selected.number}`}
                    testId="delete-proforma-button"
                    onDeleted={() => { setSelected(null); load(); }}
                  />
                  <FlagButton resource="proformas" id={selected.id} flag={selected.flag} onChanged={async (data) => { await load(); if (data) setSelected(data); else setSelected({ ...selected, flag: null }); }} testId="flag-proforma-button" />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CreateLinkedDocumentDialog kind="proforma" open={openCreate} onOpenChange={setOpenCreate}
        onCreated={(pf) => { setOpenCreate(false); load(); setSelected(pf); }} />
      <SQLRefDialog pf={sqlDlg} onOpenChange={(o) => !o && setSqlDlg(null)}
                    onSaved={async () => { setSqlDlg(null); await load(); if (selected) setSelected({ ...selected }); }} />
    </>
  );
}

function SQLRefDialog({ pf, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(pf?.sql_ref || {}); }, [pf]);
  if (!pf) return null;
  const save = async () => {
    if (!form.sql_invoice_number || !form.issued_date) { toast.error("Invoice number and issued date required"); return; }
    setSaving(true);
    try {
      await api.post(`/proformas/${pf.id}/sql-ref`, form);
      toast.success("SQL reference recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!pf} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>SQL Accounting reference</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">SQL Invoice number</Label>
            <Input value={form.sql_invoice_number || ""} onChange={(e) => setForm({ ...form, sql_invoice_number: e.target.value })} data-testid="sql-invoice-number" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Issued date</Label>
            <Input type="date" value={form.issued_date || ""} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">e-Invoice reference (optional)</Label>
            <Input value={form.einvoice_reference || ""} onChange={(e) => setForm({ ...form, einvoice_reference: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-sql-ref">{saving ? "Saving…" : "Record reference"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-0.5 text-slate-900 text-sm">{children}</div>
    </div>
  );
}
